import type { Bot } from "@rakazo/contracts";
import { Button, Textarea } from "@rakazo/ui-web";
import { useEffect, useId, useRef, useState } from "react";
import { errorText } from "../../lib/user-error";

export function CustomerServiceInstructions({
  bot,
  enabled,
  onSave,
}: {
  bot: Bot;
  enabled: boolean;
  onSave: (patch: { serviceInstructions: string }) => Promise<void>;
}) {
  const id = useId();
  const [text, setText] = useState(bot.serviceInstructions ?? "");
  const accepted = useRef(bot.serviceInstructions ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const next = bot.serviceInstructions ?? "";
    setText((current) => (current === accepted.current ? next : current));
    accepted.current = next;
  }, [bot.serviceInstructions]);
  if (!enabled) return null;
  return (
    <section className="mt-3 space-y-3 rounded-xl border p-4">
      <label htmlFor={id} className="block text-sm font-medium">
        Customer service instructions
      </label>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        For this bot’s customer conversations. Guide tone, escalation, follow-up and ticket
        handling.
      </p>
      <Textarea
        id={id}
        aria-describedby={`${id}-hint`}
        className="min-h-40"
        maxLength={20000}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="How should this rep help customers?"
        disabled={saving}
      />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={saving || text.trim() === accepted.current}
          onClick={async () => {
            setSaving(true);
            setError("");
            const next = text.trim();
            try {
              await onSave({ serviceInstructions: next });
              accepted.current = next;
              setText(next);
            } catch (cause) {
              setError(errorText(cause));
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? "Saving…" : "Save instructions"}
        </Button>
      </div>
    </section>
  );
}
